/**
 * D-096 — the widget's stylesheet, injected into its closed shadow root.
 *
 * Palette is the Dialo brand system (D-070): signal green on midnight, matching
 * `apps/portal-web/src/app/globals.css`. Kept as a TS template string rather than a .css file so the bundle is a
 * single self-contained script — a store includes one tag and nothing else.
 *
 * Deliberate choices:
 *  · every selector is scoped under `.dialo`, and the shadow root means even that is belt-and-braces;
 *  · no webfont is loaded. Pulling Peyda onto a customer's storefront would cost a round trip and a CORS header for
 *    a few hundred pixels of chrome, so the widget inherits the shop's own Persian stack and falls back to Tahoma,
 *    which every Iranian visitor already has;
 *  · `prefers-reduced-motion` turns off the pulse and the dots — a bubble that animates forever is an accessibility
 *    problem on a page someone is trying to read;
 *  · the panel is a fixed sheet on phones (where most Iranian storefront traffic is) and a floating card above.
 */
export const STYLES = `
.dialo {
  --signal: #c6f36b;
  --midnight: #101312;
  --graphite: #1b211f;
  --soft: #f5f7f2;
  --gray: #969e98;
  --border: #2a322e;
  --radius: 0.75rem;
  position: fixed;
  inset-inline-end: 20px;
  inset-block-end: 20px;
  z-index: 2147483000;
  font-family: Peyda, Vazirmatn, IRANSans, Tahoma, system-ui, sans-serif;
  font-size: 14px;
  line-height: 1.7;
  direction: rtl;
}
.dialo *, .dialo *::before, .dialo *::after { box-sizing: border-box; }

/* ---- launcher ---- */
.fab {
  position: relative;
  width: 64px; height: 64px;
  border: 0; border-radius: 50%;
  padding: 0; cursor: pointer;
  background: var(--midnight);
  box-shadow: 0 10px 30px -8px rgba(0,0,0,.55), 0 0 0 1px rgba(198,243,107,.35);
  display: grid; place-items: center;
  transition: transform .18s ease, box-shadow .18s ease;
}
.fab:hover { transform: translateY(-2px); box-shadow: 0 14px 34px -8px rgba(0,0,0,.6), 0 0 0 1px rgba(198,243,107,.6); }
.fab:focus-visible { outline: 2px solid var(--signal); outline-offset: 3px; }
.orb { width: 56px; height: 56px; display: block; }

/* CSS fallback orb: used only when the .riv cannot load (same states as the Rive one). */
.orb-fallback { display: none; width: 26px; height: 26px; border-radius: 50%; border: 2px solid var(--signal); }
.orb-fallback.on { display: block; }
.orb-fallback[data-state="connecting"], .orb-fallback[data-state="thinking"] { animation: dialo-pulse 1.2s ease-in-out infinite; }
.orb-fallback[data-state="speaking"] { animation: dialo-pulse .6s ease-in-out infinite; background: rgba(198,243,107,.25); }
.orb-fallback[data-state="error"] { border-color: #f0705a; }
@keyframes dialo-pulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.18); opacity: .65; } }

/* ---- panel ---- */
.panel {
  position: absolute;
  inset-inline-end: 0; inset-block-end: 78px;
  width: 360px; max-height: min(70vh, 560px);
  display: flex; flex-direction: column;
  background: var(--graphite); color: var(--soft);
  border: 1px solid var(--border); border-radius: var(--radius);
  box-shadow: 0 24px 60px -16px rgba(0,0,0,.7);
  overflow: hidden;
  animation: dialo-in .16s ease-out;
}
.panel[hidden] { display: none; }
@keyframes dialo-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }

.head { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 12px 14px; border-bottom: 1px solid var(--border); background: var(--midnight); }
.who { display: flex; flex-direction: column; min-width: 0; }
.who strong { font-size: 14px; font-weight: 600; }
.who .org { font-size: 11px; color: var(--gray); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.actions { display: flex; gap: 4px; flex: 0 0 auto; }
.actions button { width: 32px; height: 32px; display: grid; place-items: center; background: transparent; color: var(--gray); border: 0; border-radius: 8px; cursor: pointer; }
.actions button:hover { background: rgba(255,255,255,.06); color: var(--soft); }
.actions button:focus-visible { outline: 2px solid var(--signal); outline-offset: 1px; }
.actions .voice.live { background: rgba(198,243,107,.15); color: var(--signal); }

.status { padding: 0 14px; font-size: 11px; color: var(--gray); max-height: 0; overflow: hidden; transition: max-height .15s ease, padding .15s ease; }
.status.on { max-height: 40px; padding: 8px 14px 0; }

.log { flex: 1 1 auto; overflow-y: auto; overscroll-behavior: contain; padding: 14px; display: flex; flex-direction: column; gap: 8px; }
.log:focus-visible { outline: none; }
.msg { max-width: 85%; padding: 8px 12px; border-radius: 14px; white-space: pre-wrap; overflow-wrap: anywhere; }
.msg.agent { align-self: flex-start; background: #232b28; border-start-start-radius: 4px; }
.msg.user { align-self: flex-end; background: var(--signal); color: #0c1a08; border-start-end-radius: 4px; }
.msg a { color: inherit; text-decoration: underline; text-underline-offset: 2px; }
.msg.typing { padding: 12px; }
.dots { display: inline-flex; gap: 4px; }
.dots i { width: 6px; height: 6px; border-radius: 50%; background: var(--gray); animation: dialo-dot 1.1s infinite ease-in-out; }
.dots i:nth-child(2) { animation-delay: .15s; }
.dots i:nth-child(3) { animation-delay: .3s; }
@keyframes dialo-dot { 0%,80%,100% { opacity: .3; transform: translateY(0); } 40% { opacity: 1; transform: translateY(-3px); } }

.composer { display: flex; align-items: flex-end; gap: 8px; padding: 10px; border-top: 1px solid var(--border); background: var(--midnight); }
.composer textarea {
  flex: 1 1 auto; resize: none; max-height: 120px;
  padding: 9px 12px; font: inherit; color: var(--soft);
  background: #141a18; border: 1px solid var(--border); border-radius: 10px;
}
.composer textarea:focus { outline: none; border-color: var(--signal); }
.composer textarea::placeholder { color: var(--gray); }
.send { width: 38px; height: 38px; flex: 0 0 auto; display: grid; place-items: center; background: var(--signal); color: #0c1a08; border: 0; border-radius: 10px; cursor: pointer; }
.send:hover { filter: brightness(1.06); }
.send:focus-visible { outline: 2px solid var(--soft); outline-offset: 2px; }
/* the send glyph points along the reading direction */
.send svg { transform: scaleX(-1); }

.footer { padding: 6px 14px 10px; font-size: 10px; color: var(--gray); text-align: center; background: var(--midnight); }

@media (max-width: 480px) {
  .dialo { inset-inline-end: 12px; inset-block-end: 12px; }
  .panel { position: fixed; inset: 0; width: auto; max-height: none; border-radius: 0; border: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .dialo *, .panel { animation: none !important; transition: none !important; }
  .fab:hover { transform: none; }
}
`;
